import { useMemo } from 'react';
import { localDate, type DateRange, type LocalDate } from '@tahi/core';
import { commitmentTables, loadCommitments, loadPlanned } from '../../data/commitments';
import { useData, useUsable } from '../../data/hooks';
import type { LocalDb } from '../../data/localDb';
import * as repo from '../../data/repos/budget';
import { listEntriesInRange } from '../../data/repos/spending';
import { useQuery } from '../../data/useQuery';
import { useWeekStart } from '../spending/hooks';
import { todayWindow, type BudgetSnapshot } from './derive';

/** v0.1c's useQuery (re-runs when one of the tables changes), returning just the data. */
function useRead<T>(
  tables: readonly string[],
  read: (db: LocalDb, userId: string) => Promise<T>,
  deps: readonly unknown[],
): T | undefined {
  const { userId } = useData();
  return useQuery(tables, (db) => read(db, userId), [userId, ...deps]).data;
}

export const useFunds = () => useRead(['funds'], repo.listFunds, []);
export const useFund = (id: string) => useRead(['funds'], (db, u) => repo.getFund(db, u, id), [id]);
export const useBudgetSettings = () =>
  useRead(['budget_settings', 'funds', 'money_accounts'], repo.getBudgetSettingsView, []);
export const useIncomeSources = () =>
  useRead(['income_sources'], (db, u) => repo.listIncomeSources(db, u), []);
export const useOtherIncome = (id: string) =>
  useRead(['ledger_entries'], (db, u) => repo.getOtherIncome(db, u, id), [id]);
export const useAccounts = () => useRead(['money_accounts'], repo.listAccounts, []);
export const useExpectedMoney = () =>
  useRead(['expected_money', 'expected_receipts'], repo.listExpected, []);
export const useReceivables = () => useRead(['receivables'], repo.listReceivables, []);
export const useIncomeSource = (id: string) =>
  useRead(['income_sources'], (db, u) => repo.getIncomeSource(db, u, id), [id]);

/** Today in the user's time zone. */
export function useToday(): LocalDate {
  return localDate(new Date(), useData().tz);
}

const BASE_TABLES = [
  'funds',
  'budget_settings',
  'income_sources',
  'paydays',
  'ledger_entries',
  'money_accounts',
  'expected_money',
  'expected_receipts',
  'receivables',
] as const;

/** Everything Budget's numbers need for `range`; undefined while loading. */
export function useBudgetSnapshot(range: DateRange): BudgetSnapshot | undefined {
  const { userId } = useData();
  const usable = useUsable();
  const weekStart = useWeekStart();
  const today = useToday();
  const { data } = useQuery(
    [...BASE_TABLES, ...commitmentTables()],
    async (db) => {
      const sources = await repo.listIncomeSources(db, userId, { includeDeleted: true });
      const rows = await repo.listPaydayRows(db, userId);
      const funds = await repo.listFunds(db, userId);
      const settings = await repo.getBudgetSettingsView(db, userId);
      const entries = await listEntriesInRange(db, userId, range);
      const commitments = await loadCommitments(db, userId, range, usable, today);
      const planned = await loadPlanned(db, userId, usable);
      const accounts = await repo.listAccounts(db, userId);
      const accountEntries = await repo.listAccountEntries(db, userId);
      const expected = await repo.listExpected(db, userId);
      const receivables = await repo.listReceivables(db, userId);
      const receipts = await repo.listBudgetDeposits(db, userId);
      return {
        sources,
        rows,
        funds,
        settings,
        entries,
        commitments,
        planned,
        accounts,
        accountEntries,
        expected,
        receivables,
        receipts,
      };
    },
    [userId, range.start, range.end, usable, today],
  );
  // Callers memoize `range` (useBudgetToday, CashFlowScreen), so it is a stable dependency.
  return useMemo(
    () => (data ? { userId, today, weekStart, window: range, ...data } : undefined),
    [data, userId, today, weekStart, range],
  );
}

/** The snapshot for today's numbers (tiles, dashboard, funds, income). */
export function useBudgetToday(): BudgetSnapshot | undefined {
  const today = useToday();
  const range = useMemo(() => todayWindow(today), [today]);
  return useBudgetSnapshot(range);
}
